/** @jsx jsx */
import { jsx } from '@emotion/core';
import cmsUtils from '../../cms/utils/cmsUtils';
import { useEffect, useRef } from 'react';

export function Expr3ssJobWidget(props) {
  const item = props.item;
  const cssClass = props.cssClass;
  const code = cmsUtils.payload(item, 'Code') || '';
  const defaultWidgetId = cmsUtils.payload(item, 'WidgetId') || 'expr3ss-jobs-widget';
  const containerRef = useRef(null);
  
  // Extract widget ID from code or use default
  const divIdMatch = code.match(/id=['"]([^'"]+)['"]/);
  const widgetId = divIdMatch ? divIdMatch[1] : defaultWidgetId;

  // Helper functions to extract attributes from code
  const extractAttribute = (html, attrName) => {
    const regex = new RegExp(`data-${attrName}="([^"]+)"`, 'i');
    const match = html.match(regex);
    return match ? match[1] : null;
  };

  const extractArrayAttribute = (html, attrName) => {
    const value = extractAttribute(html, attrName);
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return value.split(',').map(v => v.trim());
    }
  };

  const extractBooleanAttribute = (html, attrName) => {
    const value = extractAttribute(html, attrName);
    if (value === null) return null;
    return value.toLowerCase() === 'true';
  };

  useEffect(() => {
    // Parse widget configuration from code
    let widgetConfig = null;

    // Try to extract the Expr3ss.Widget configuration from the code
    // Look for Expr3ss.Widget = {...} pattern in script tags
    const scriptMatches = code.match(/<script[^>]*>([\s\S]*?)<\/script>/g);
    if (scriptMatches) {
      for (const scriptTag of scriptMatches) {
        const scriptContent = scriptTag.match(/<script[^>]*>([\s\S]*?)<\/script>/)[1];
        const configMatch = scriptContent.match(/Expr3ss\.Widget\s*=\s*({[\s\S]*?});/);
        if (configMatch) {
          try {
            // Use eval-like approach to parse the object, but safer
            // Replace the object literal with JSON-compatible format
            let configStr = configMatch[1];
            // Handle unquoted keys in object literal
            configStr = configStr.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
            widgetConfig = JSON.parse(configStr);
            break;
          } catch (e) {
            // If JSON parsing fails, try to evaluate it safely
            try {
              // Create a function that returns the config object
              const func = new Function('return ' + configMatch[1]);
              widgetConfig = func();
            } catch (e2) {
              console.error('Failed to parse Expr3ss widget configuration:', e2);
            }
          }
        }
      }
    }

    // If no config found, try to parse the entire code as JSON
    if (!widgetConfig) {
      try {
        widgetConfig = JSON.parse(code);
      } catch (e) {
        // Not JSON, continue with defaults
      }
    }

    // If still no config found, use defaults or extract from code attributes
    if (!widgetConfig) {
      widgetConfig = {
        config: {
          token: extractAttribute(code, 'token') || cmsUtils.payload(item, 'Token') || 'a28eb30008340d4633acd1f9a3db1f5f',
          parent: extractAttribute(code, 'parent') || cmsUtils.payload(item, 'Parent') || 'body',
          id: widgetId,
          sort: extractAttribute(code, 'sort') || cmsUtils.payload(item, 'Sort') || 'division:asc,title:asc',
          grouping: extractAttribute(code, 'grouping') || cmsUtils.payload(item, 'Grouping') || 'division',
          button: {
            label: extractArrayAttribute(code, 'buttonLabel') || ['Apply Now', 'Interest Only'],
            colour: extractArrayAttribute(code, 'buttonColour') || ['#004b91', '#433125', '#ffffff']
          },
          collapsed: extractBooleanAttribute(code, 'collapsed') ?? false,
          noDescription: extractBooleanAttribute(code, 'noDescription') ?? false,
          hideInterest: extractBooleanAttribute(code, 'hideInterest') ?? false,
          hideApply: extractBooleanAttribute(code, 'hideApply') ?? false,
          sourceTag: extractAttribute(code, 'sourceTag') || cmsUtils.payload(item, 'SourceTag') || '6'
        }
      };
    }

    // Ensure the config has the correct structure
    if (!widgetConfig.config) {
      widgetConfig = { config: widgetConfig };
    }

    // Update the widget ID in config to match the actual div ID
    if (widgetConfig.config) {
      widgetConfig.config.id = widgetId;
    }

    // Initialize Expr3ss if not already initialized
    if (typeof window.Expr3ss === 'undefined') {
      window.Expr3ss = [];
    }

    // Set widget configuration BEFORE loading scripts
    // This is critical - the widget script expects this to be set when it loads
    window.Expr3ss.Widget = widgetConfig;

    // Load jQuery if not already loaded
    const loadJQuery = () => {
      return new Promise((resolve) => {
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.jquery) {
          resolve();
          return;
        }

        const jqueryScript = document.createElement('script');
        jqueryScript.type = 'text/javascript';
        jqueryScript.src = 'https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js';
        jqueryScript.onload = () => {
          resolve();
        };
        jqueryScript.onerror = () => {
          resolve(); // Continue even if jQuery fails
        };
        document.head.appendChild(jqueryScript);
      });
    };

    // Load widget script
    const loadWidgetScript = () => {
      return new Promise((resolve) => {
        const existingScript = document.querySelector('script[src*="widget.min.js"]');
        
        if (existingScript) {
          // Script already loaded, need to manually trigger initialization
          resolve(true); // true indicates script was already loaded
          return;
        }

        const widgetScript = document.createElement('script');
        widgetScript.type = 'text/javascript';
        widgetScript.src = 'https://developers.expr3ss.com/Files/js/widget.min.js';
        widgetScript.onload = () => {
          resolve(false); // false indicates new load
        };
        widgetScript.onerror = () => {
          resolve(false);
        };
        document.head.appendChild(widgetScript);
      });
    };

    // Add error handler for API failures
    const handleApiError = () => {
      const widgetDiv = document.getElementById(widgetId);
      if (widgetDiv && widgetDiv.innerHTML.length === 0) {
        const currentToken = window.Expr3ss?.Widget?.config?.token || 'unknown';
        const tokenPreview = currentToken.length > 20 ? currentToken.substring(0, 20) + '...' : currentToken;
        
        // Show user-friendly error message
        widgetDiv.innerHTML = `
          <div style="padding: 20px; border: 1px solid #e74c3c; background: #fadbd8; color: #c0392b; border-radius: 4px; margin: 10px 0;">
            <h4 style="margin: 0 0 10px 0; color: #c0392b;">⚠️ Unable to Load Job Listings</h4>
            <p style="margin: 0 0 10px 0;">The job widget could not connect to the Expr3ss API. This is likely due to an invalid or expired API token.</p>
            <p style="margin: 0; font-size: 0.9em;"><strong>Error:</strong> 401 Unauthorized</p>
            <p style="margin: 5px 0; font-size: 0.85em; color: #666;"><strong>Current Token:</strong> ${tokenPreview}</p>
            <p style="margin: 10px 0 0 0; font-size: 0.9em;"><em>Please contact your site administrator to update the API token in the CMS.</em></p>
          </div>
        `;
      }
    };

    // Initialize the widget
    const initWidget = () => {
      if (!window.Expr3ss) {
        return;
      }

      // The widget script auto-initializes when it loads if window.Expr3ss.Widget is set
      // Check if widget was initialized by looking for content in the div
      const widgetDiv = document.getElementById(widgetId);
      if (widgetDiv) {
        // Widget auto-initializes and calls getJobs() automatically
        // We need to wait a bit to see if it populated
        setTimeout(() => {
          if (widgetDiv.innerHTML.length === 0) {
            // Show error message to users
            handleApiError();
          }
        }, 2000); // Give it 2 seconds for the API call to complete
      }
    };

    // Load scripts in sequence
    loadJQuery()
      .then(() => loadWidgetScript())
      .then((wasAlreadyLoaded) => {
        // Give a bit more time if script was just loaded
        const delay = wasAlreadyLoaded ? 100 : 500;
        setTimeout(() => {
          initWidget();
        }, delay);
      })
      .catch(err => {
        // Error loading scripts
      });

    // Cleanup function
    return () => {
      // Clean up the widget if it has a destroy method
      if (window.jQuery && window.jQuery.fn && typeof window.jQuery.fn.expr3ss === 'function') {
        const $widget = window.jQuery(`#${widgetId}`);
        if ($widget.length && typeof $widget.expr3ss === 'function') {
          // Some jQuery plugins have a destroy method
          try {
            $widget.empty();
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }
    };
  }, [code, widgetId, item]);

  return (
    <div className={cssClass} id={item.anchorName} ref={containerRef}>
      <div id={widgetId}></div>
    </div>
  );
}

